import { DateTime } from 'luxon';

import { usePreferences } from '@/stores/preferences-store';
import { cn } from '@/utils/cn';

import { DatePicker } from './date-picker';
import type { PopoverPortalTarget } from './popover-portal';
import { TimePicker } from './time-picker';

/**
 * Authors an instant in the church's timezone and emits UTC ISO 8601.
 *
 * The visible timezone line is the point of this component. A pastor in Accra
 * scheduling a 7:00 AM service while an administrator in London edits the same record
 * must both mean 7:00 in Accra. Without the label, whoever is travelling silently
 * moves the service.
 *
 * Reference: documentation/architecture/widget-architecture.md §3.3.
 */

export interface DateTimePickerProps {
  /** UTC ISO 8601, or null. */
  value: string | null;
  onChange: (utcIso: string | null) => void;
  /** Overrides the church timezone — used when editing another branch's event. */
  timezone?: string;
  /** The line under the fields; the caller supplies the wording and the zone name. */
  timezoneNotice?: (timezone: string) => string;
  datePlaceholder?: string;
  minDate?: string;
  maxDate?: string;
  minuteStep?: number;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  popoverPortal?: PopoverPortalTarget;
  labels?: {
    hour?: string;
    minute?: string;
    meridiem?: string;
    month?: string;
    year?: string;
    previousMonth?: string;
    nextMonth?: string;
  };
  className?: string;
}

export const DateTimePicker = ({
  value,
  onChange,
  timezone,
  timezoneNotice,
  datePlaceholder,
  minDate,
  maxDate,
  minuteStep = 5,
  disabled = false,
  invalid = false,
  id,
  popoverPortal = 'body',
  labels,
  className,
}: DateTimePickerProps) => {
  const churchTimezone = usePreferences((state) => state.timezone);
  const zone = timezone ?? churchTimezone;

  const local = value ? DateTime.fromISO(value, { zone: 'utc' }).setZone(zone) : null;
  const date = local?.isValid ? local.toFormat('yyyy-MM-dd') : null;
  const time = local?.isValid ? local.toFormat('HH:mm') : null;

  const emit = (nextDate: string | null, nextTime: string | null) => {
    if (!nextDate) {
      onChange(null);
      return;
    }

    // Defaulting the time rather than refusing to emit: a user who picks a date and
    // saves should get midnight in their own zone, not a silently discarded field.
    const composed = DateTime.fromISO(`${nextDate}T${nextTime ?? '00:00'}`, { zone });
    if (!composed.isValid) return;

    onChange(composed.toUTC().toISO());
  };

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="min-w-0 flex-1">
          <DatePicker
            id={id}
            value={date}
            onChange={(next) => emit(next, time)}
            placeholder={datePlaceholder}
            min={minDate}
            max={maxDate}
            disabled={disabled}
            invalid={invalid}
            popoverPortal={popoverPortal}
            calendarLabels={{
              month: labels?.month,
              year: labels?.year,
              previousMonth: labels?.previousMonth,
              nextMonth: labels?.nextMonth,
            }}
          />
        </div>

        <TimePicker
          value={time}
          onChange={(next) => emit(date, next)}
          minuteStep={minuteStep}
          disabled={disabled || !date}
          invalid={invalid}
          hourLabel={labels?.hour}
          minuteLabel={labels?.minute}
          meridiemLabel={labels?.meridiem}
          popoverPortal={popoverPortal}
        />
      </div>

      {timezoneNotice ? (
        <p className="text-caption text-fg-secondary">{timezoneNotice(zone)}</p>
      ) : null}
    </div>
  );
};
